import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, toast } from '@mlm/ui';
import { Trash2 } from 'lucide-react';
import { STOCKIST_CONSIGNMENT_PERMS } from '@mlm/shared';
import {
  createStockistConsignment,
  listPosProducts,
  listStockistBranches,
  type PosProduct,
  type StockistBranchRow,
} from '../../lib/api';
import { useActiveBranch } from '../../lib/use-branch';
import { BranchPicker } from '../../components/branch-picker';
import { useHasPerm } from '../../lib/auth-store';

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type Line = {
  key: string;
  productId: string;
  variantId: string | null;
  name: string;
  variantName: string | null;
  available: number;
  qty: number;
  unitPrice: string;
};

/**
 * The refusals this form has to explain in the cashier's own words. A stockist
 * may only be consigned an item they hold ZERO of — otherwise "did they sell
 * ours or theirs?" has no answer, which is the rule the whole module rests on.
 */
function apiError(e: unknown): string {
  const code = isAxiosError(e)
    ? ((e.response?.data as { error?: string } | undefined)?.error ?? 'request_failed')
    : 'request_failed';
  const base = code.split(':')[0]!;
  switch (base) {
    case 'stockist_has_stock':
      return 'That stockist already owns stock of this item. Consign a different item, or wait until they have sold out.';
    case 'stockist_has_consigned_stock':
      return 'That stockist still holds consigned units of this item. Return or sell those through first.';
    case 'out_of_stock':
      return 'Not enough stock at the source branch.';
    case 'self_consignment':
      return 'Pick a different source branch — a stockist cannot consign to itself.';
    case 'branch_not_stockist':
      return 'That branch is not a stockist.';
    case 'idempotency_race_retry':
      return 'That looked like a double submit. Check the list before trying again.';
    default:
      return 'Could not record the consignment.';
  }
}

/**
 * Consign-Out (SCN-): hand house stock to a STOCKIST branch. Stock leaves the
 * source branch, lands in the stockist's own inventory so their POS can sell
 * it, and is tracked as a consigned holding. No receivable yet — that is raised
 * automatically when they sell it.
 */
export function StockistConsignOutPage() {
  const navigate = useNavigate();
  const canConsignOut = useHasPerm(STOCKIST_CONSIGNMENT_PERMS.consignOut);
  const { branchId, isScoped, branches, setSelectedBranchId } = useActiveBranch();

  const [stockistQuery, setStockistQuery] = useState('');
  const [stockist, setStockist] = useState<StockistBranchRow | null>(null);
  const stockistsQuery = useQuery({
    queryKey: ['stockist-branches', stockistQuery],
    queryFn: () => listStockistBranches(stockistQuery || undefined),
    enabled: !stockist,
  });

  const productsQuery = useQuery({
    queryKey: ['pos-products', branchId],
    queryFn: () => listPosProducts(branchId!),
    enabled: !!branchId,
  });
  const [productQuery, setProductQuery] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [memo, setMemo] = useState('');
  const [done, setDone] = useState<{ docNo: string } | null>(null);

  const matches = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return [];
    return (productsQuery.data ?? [])
      .filter(
        (p) =>
          !p.isVirtual && (p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)),
      )
      .slice(0, 8);
  }, [productQuery, productsQuery.data]);

  function addProduct(p: PosProduct, variantId: string | null) {
    const variant = variantId ? p.variants.find((v) => v.id === variantId) : null;
    const key = `${p.id}:${variantId ?? ''}`;
    if (lines.some((l) => l.key === key)) {
      toast.info('Already on the list — adjust its quantity instead.');
      return;
    }
    setLines((prev) => [
      ...prev,
      {
        key,
        productId: p.id,
        variantId,
        name: p.name,
        variantName: variant?.name ?? null,
        available: variant ? variant.stockQty : p.qty,
        qty: 1,
        unitPrice: variant ? variant.price : p.effectivePrice,
      },
    ]);
    setProductQuery('');
  }

  const totalUnits = lines.reduce((s, l) => s + l.qty, 0);
  const totalValue = lines.reduce((s, l) => s + Number(l.unitPrice || 0) * l.qty, 0);

  const submit = useMutation({
    mutationFn: () =>
      createStockistConsignment({
        stockistBranchId: stockist!.id,
        ...(isScoped ? {} : { branchId: branchId ?? undefined }),
        lines: lines.map((l) => ({
          productId: l.productId,
          variantId: l.variantId,
          qty: l.qty,
          unitPrice: l.unitPrice,
        })),
        memo: memo.trim() || null,
      }),
    onSuccess: (header) => setDone(header),
    onError: (e) => toast.error(apiError(e)),
  });

  const canSubmit =
    !!stockist &&
    !!branchId &&
    lines.length > 0 &&
    // The price is what the stockist will be BILLED at when it sells, so a zero
    // is never a harmless default here — the server rejects it too.
    lines.every((l) => l.qty > 0 && Number(l.unitPrice) > 0) &&
    !submit.isPending;

  if (!canConsignOut) {
    return <p className="text-sm text-muted-foreground">You do not have access to consign out.</p>;
  }

  if (done) {
    return (
      <Card className="mx-auto max-w-lg">
        <CardHeader>
          <CardTitle>Consignment recorded</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-mono font-medium text-foreground">{done.docNo}</span> —{' '}
            {totalUnits} unit{totalUnits === 1 ? '' : 's'} now with{' '}
            <span className="font-medium text-foreground">{stockist?.name}</span>. They will be
            billed automatically as these sell.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate(`/stockist-consignment/${stockist!.id}`)}>
              Go to stockist
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDone(null);
                setLines([]);
                setMemo('');
              }}
            >
              Consign more
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 pb-28">
      <div>
        <h1 className="text-2xl font-semibold">Consign Out to a Stockist</h1>
        <p className="text-sm text-muted-foreground">
          Stock moves into the stockist&apos;s own inventory so their till can sell it. The
          receivable is raised automatically at the price you set here, when it sells.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Stockist</CardTitle>
        </CardHeader>
        <CardContent>
          {stockist ? (
            <div className="flex items-center gap-3">
              <div>
                <p className="font-medium">{stockist.name}</p>
                <p className="text-sm text-muted-foreground">{stockist.code}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setStockist(null)}>
                Change
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                value={stockistQuery}
                onChange={(e) => setStockistQuery(e.target.value)}
                placeholder="Search stockist by name / code…"
                className="max-w-md"
              />
              <div className="max-w-md divide-y rounded-md border">
                {(stockistsQuery.data ?? []).slice(0, 8).map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/50"
                    onClick={() => setStockist(b)}
                  >
                    <span className="font-medium">{b.name}</span>
                    <span className="text-muted-foreground">{b.code}</span>
                  </button>
                ))}
                {(stockistsQuery.data ?? []).length === 0 && !stockistsQuery.isLoading && (
                  <p className="px-3 py-3 text-sm text-muted-foreground">No stockist branches.</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Items</CardTitle>
            <BranchPicker
              branches={branches}
              branchId={branchId}
              isScoped={isScoped}
              onChange={(id) => setSelectedBranchId(id)}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative max-w-md">
            <Input
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              placeholder="Search product by name / SKU…"
              disabled={!branchId}
            />
            {matches.length > 0 && (
              <div className="absolute z-10 mt-1 w-full divide-y rounded-md border bg-background shadow-md">
                {matches.map((p) =>
                  p.variants.length > 0 ? (
                    <div key={p.id} className="px-3 py-2 text-sm">
                      <p className="font-medium">{p.name}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {p.variants.map((v) => (
                          <button
                            key={v.id}
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-40"
                            disabled={v.stockQty <= 0}
                            onClick={() => addProduct(p, v.id)}
                          >
                            {v.name} · {v.stockQty} on hand
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      key={p.id}
                      type="button"
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/50 disabled:opacity-40"
                      disabled={p.qty <= 0}
                      onClick={() => addProduct(p, null)}
                    >
                      <span className="font-medium">{p.name}</span>
                      <span className="text-muted-foreground">
                        {p.qty} on hand · {peso(p.effectivePrice)}
                      </span>
                    </button>
                  ),
                )}
              </div>
            )}
          </div>

          {lines.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Search a product above to add it.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3">Product</th>
                    <th className="py-2 pr-3 text-right">On hand</th>
                    <th className="py-2 pr-3">Qty</th>
                    <th className="py-2 pr-3">Billed at</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.key} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <span className="font-medium">{l.name}</span>
                        {l.variantName ? (
                          <span className="text-muted-foreground"> · {l.variantName}</span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-3 text-right text-muted-foreground">{l.available}</td>
                      <td className="py-2 pr-3">
                        <Input
                          type="number"
                          min={1}
                          max={l.available}
                          value={l.qty}
                          className="w-20"
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((x) =>
                                x.key === l.key
                                  ? {
                                      ...x,
                                      qty: Math.max(
                                        1,
                                        Math.min(l.available, Number(e.target.value) || 1),
                                      ),
                                    }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <Input
                          inputMode="decimal"
                          value={l.unitPrice}
                          className="w-28"
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((x) =>
                                x.key === l.key
                                  ? { ...x, unitPrice: e.target.value.replace(/[^0-9.]/g, '') }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="py-2 pr-3 text-right font-medium">
                        {peso(Number(l.unitPrice || 0) * l.qty)}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="max-w-md space-y-1">
            <label className="text-xs text-muted-foreground">Memo (optional)</label>
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Notes…" />
          </div>
        </CardContent>
      </Card>

      <div className="fixed inset-x-0 bottom-0 border-t bg-background/95 p-3 backdrop-blur">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3">
          <p className="text-sm">
            <span className="font-medium">{totalUnits}</span> unit{totalUnits === 1 ? '' : 's'} ·{' '}
            <span className="font-medium">{peso(totalValue)}</span> to be billed as it sells
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/stockist-consignment')}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Saving…' : 'Consign Out'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
